// --- START OF FILE KickoffTokensView.js ---
/* frontend/src/components/KickoffTokensView.js */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { apiService } from "../services/api";
import ObjectiveItem from "./ObjectiveItem"; // Assurez-vous que ce chemin est correct
import { useObjectiveRewrites } from "./Muse/MuseRewrite";
import { Sparkles, X } from 'lucide-react'; // Importer les icônes

const presetTokens = [1, 2, 3, 5, 8, 13, 20];

const KickoffTokensView = ({
  projectId: propProjectId,
  variant = "standalone",
  refreshToken = 0,
  onKickoffSync,
}) => {
  const projectId = Number(propProjectId || localStorage.getItem("objectiveBoard_selectedProject") || "1");
  const isEmbedded = variant === "panel";
  const [objectives, setObjectives] = useState([]);
  const [inputObjective, setInputObjective] = useState(null);
  const [customToken, setCustomToken] = useState("");
  const [hoveredBlock, setHoveredBlock] = useState(null);
  const [sprints, setSprints] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState(null);

  // --- NOUVEAU : États pour la prédiction de titre ---
  const [predictedTitle, setPredictedTitle] = useState(null);
  const [titleSuggestionDismissed, setTitleSuggestionDismissed] = useState(false);
  const objectivesRef = useRef(objectives);
  objectivesRef.current = objectives;
  const museRewrites = useObjectiveRewrites({
    projectId, objectivesRef, setObjectives, onChange: () => onKickoffSync && onKickoffSync("objectives"),
  });

  // Fetch objectives
  useEffect(() => {
    if (!projectId || isNaN(projectId)) return;
    setError(null);
    apiService.get(`/projects/${projectId}/objectives`)
      .then((data) => {
        const transformed = data.sort((a, b) => a.id - b.id).map(objective => ({ ...objective }));
        setObjectives(transformed);
      })
      .catch(err => { console.error("Error fetching objectives:", err); setError("Erreur chargement objectifs."); });
  }, [projectId, refreshToken]);

  // Fetch employees & sprints
  useEffect(() => { setError(null); apiService.get("/employees").then(setEmployees).catch(err => { console.error("Error fetching employees:", err); setError("Erreur chargement employés."); }); }, []);
  useEffect(() => { if (!projectId || isNaN(projectId)) return; setError(null); apiService.get(`/projects/${projectId}/sprints`).then(setSprints).catch(err => { console.error("Error fetching sprints:", err); setError("Erreur chargement sprints."); }); }, [projectId]);

  // Group objectives by effort value
  const { objectivesUnassigned, groupedObjectives } = useMemo(() => {
        const unassigned = objectives.filter(objective => objective.effort === 0 && !objective.fromOther && objective.status !== "done").sort((a, b) => a.id - b.id);
        const assigned = objectives.filter(objective => (objective.fromOther || objective.effort > 0) && objective.status !== "done");
        const grouped = {};
        presetTokens.forEach(token => { grouped[token] = assigned.filter(objective => objective.effort === token && !objective.fromOther).sort((a, b) => a.id - b.id); });
        grouped["other"] = assigned.filter(objective => !presetTokens.includes(objective.effort) || objective.fromOther).sort((a, b) => a.id - b.id);
        return { objectivesUnassigned: unassigned, groupedObjectives: grouped };
  }, [objectives]);

  // Drag & Drop Handlers
  const handleDragStart = (e, objectiveId) => e.dataTransfer.setData("text/plain", objectiveId);
  const handleDragOver = (e) => e.preventDefault();

  // Drop onto a token column
  const handleTokenDrop = async (e, tokenValue) => {
    e.preventDefault();
    const objectiveId = e.dataTransfer.getData("text/plain");
    let effortValue = 0; let isOther = false;
    if (tokenValue === "other") {
      const customValue = customToken.trim() === "" ? 0 : Number(customToken);
      if (isNaN(customValue)) { alert("Veuillez entrer une valeur numérique."); return; }
      effortValue = customValue; isOther = true;
    } else { effortValue = tokenValue; }
    assignTokenToObjective(objectiveId, effortValue, isOther);
  };

  // Assign token
  const assignTokenToObjective = async (objectiveId, effortValue, isOther = false) => {
    const droppedObjective = objectives.find(objective => objective.id.toString() === objectiveId);
    if (!droppedObjective) return;
    const objectiveProjectId = droppedObjective.project_id || projectId;

    setObjectives(prev => prev.map(objective => objective.id.toString() === objectiveId ? { ...objective, effort: effortValue, fromOther: isOther, x: null, y: null } : objective));
    try {
      await apiService.patch(`/projects/${objectiveProjectId}/objectives/${objectiveId}`, { effort: effortValue });
      if (onKickoffSync) onKickoffSync("objectives");
      if (isOther) setCustomToken("");
    } catch (error) {
      console.error("Erreur affectation token :", error); setError("Erreur affectation token.");
      setObjectives(prev => prev.map(t => t.id.toString() === objectiveId ? droppedObjective : t)); // Revert
    }
  };

  // Drop onto the bottom "unassigned" area
  const handleDropOnBottom = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const objectiveId = e.dataTransfer.getData("text/plain");
    const droppedObjective = objectives.find(objective => objective.id.toString() === objectiveId);
    if (!droppedObjective || droppedObjective.effort === 0) return; // Ne rien faire si déjà à 0
    const objectiveProjectId = droppedObjective.project_id || projectId;

    setObjectives(prev => prev.map(objective => objective.id.toString() === objectiveId ? { ...objective, effort: 0, fromOther: false, x: null, y: null } : objective));
    try {
      await apiService.patch(`/projects/${objectiveProjectId}/objectives/${objectiveId}`, { effort: 0 });
      if (onKickoffSync) onKickoffSync("objectives");
    } catch (error) {
      console.error("Erreur remise à zéro effort :", error); setError("Erreur désaffectation token.");
      setObjectives(prev => prev.map(t => t.id.toString() === objectiveId ? droppedObjective : t)); // Revert
    }
  };

  // Click in bottom area to add objective
  const handleBottomClick = (e) => { if (e.target.closest('.objective-item-container')) return; setInputObjective({ value: "" }); };
  const handleInputChange = (e) => {
    setInputObjective({ value: e.target.value });
    // --- NOUVEAU: Réinitialiser les prédictions si le texte change ---
    setPredictedTitle(null);
    setTitleSuggestionDismissed(false);
  };

  const handleInputBlur = async () => {
    if (!inputObjective || !inputObjective.value.trim()) {
      setInputObjective(null);
      return;
    }
    // Muse reformule le titre une fois l'objectif créé (handleSaveNewObjective).
  };
  const handleSaveNewObjective = () => {
    if (inputObjective && inputObjective.value.trim() !== "") {
      if (!projectId || isNaN(projectId)) { setError("Veuillez selectionner un projet valide."); setInputObjective(null); return; }
      const newObjectivePayload = { project_id: projectId, name: inputObjective.value.trim(), description: "", sprint_id: null, effort: 0, owner_id: null, status: "not_started", blocked_by: [], comment: "", x: null, y: null };
      const tempId = -Date.now();
      const tempObjective = { id: tempId, ...newObjectivePayload, predicted_tokens: null, fromOther: false };
      setObjectives(prev => [...prev, tempObjective].sort((a, b) => a.id - b.id));
      apiService.post(`/projects/${projectId}/objectives`, newObjectivePayload)
        .then(createdObjective => {
          setObjectives(prev => {
            const next = prev.map(t => t.id === tempId ? { ...t, ...createdObjective } : t);
            return next.slice().sort((a, b) => a.id - b.id);
          });
          if (onKickoffSync) onKickoffSync("objectives");
          museRewrites.rewrite(createdObjective); // Muse relit le titre et l'impose s'il le reformule
        })
        .catch(error => {
          console.error("Erreur creation objectif :", error);
          setError("Erreur creation objectif.");
          setObjectives(prev => prev.filter(t => t.id !== tempId));
        });
    }
    setInputObjective(null);
    setPredictedTitle(null); // Nettoyer apr?s la sauvegarde
    setTitleSuggestionDismissed(false);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveNewObjective();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setInputObjective(null);
      setPredictedTitle(null);
      setTitleSuggestionDismissed(false);
    }
  };

  // --- Render objective card - Afficher UI Token IA ---
  const renderObjectiveCard = useCallback(
    (objective) => (
      <div key={objective.id} className="objective-item-container relative" draggable={true} onDragStart={(e) => handleDragStart(e, objective.id)}>
        <ObjectiveItem
          objective={objective}
          onDelete={(id) => {
            const objectiveProjectId = objective.project_id || projectId;
            apiService
              .delete(`/projects/${objectiveProjectId}/objectives/${id}`)
              .then(() => {
                setObjectives(prev => prev.filter(t => t.id !== id));
                if (onKickoffSync) onKickoffSync("objectives");
              })
              .catch(err => { console.error("Error deleting objective:", err); setError("Erreur suppression objectif."); });
          }}
          onUpdate={(updatedFields) => {
            const objectiveProjectId = objective.project_id || projectId;
            const { x, y, ...fieldsToSend } = updatedFields;
            apiService
              .patch(`/projects/${objectiveProjectId}/objectives/${objective.id}`, fieldsToSend)
              .then(response => {
                setObjectives(prev => prev.map(t => (t.id === response.id ? { ...t, ...response } : t)));
                if (onKickoffSync) onKickoffSync("objectives");
              })
              .catch(err => { console.error("Erreur mise a jour :", err); setError("Erreur mise a jour objectif."); });
          }}
          // onDragStart enleve d'ici, mis sur le wrapper
          onDragOver={(e) => e.preventDefault()}
          sprintOptions={sprints} ownerOptions={employees}
          // Afficher l'UI du token IA ici
          showPredictedTokenUI={true}
          predictedTokens={objective.predicted_tokens}
          onPredictedTokenClick={(e) => { e.stopPropagation(); if (objective.predicted_tokens !== undefined && objective.predicted_tokens !== null) { assignTokenToObjective(objective.id.toString(), objective.predicted_tokens); } }}
          onRequestSuggestion={(id, name) => museRewrites.rewrite({ id, name })}
          museRewrite={museRewrites.rewrites[objective.id]}
          museThinking={!!museRewrites.thinking[objective.id]}
          onUndoRewrite={museRewrites.undo}
          onRewriteSettled={museRewrites.settle}
        />
      </div>
    ),
    [projectId, sprints, employees, objectives, assignTokenToObjective, onKickoffSync, museRewrites]
  );

  const containerClass = isEmbedded ? "flex flex-col gap-4" : "h-screen flex flex-col";
  const boardClass = isEmbedded
    ? "rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm overflow-x-auto overflow-y-hidden flex space-x-4 no-scrollbar"
    : "h-[45%] bg-white p-4 border-b overflow-x-auto overflow-y-hidden flex space-x-4 no-scrollbar";
  const backlogClass = isEmbedded
    ? "relative rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 p-4 overflow-y-auto no-scrollbar"
    : "h-[35%] relative bg-gray-50 p-4 border border-dashed border-gray-300 rounded overflow-y-auto no-scrollbar";

  return (
    <div className={containerClass}>
      {error && (<div className="mb-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700"><button onClick={() => setError(null)} className="float-right font-bold text-red-900">×</button>{error}</div>)}
      {/* Token Columns */}
      <div className={boardClass} style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
        {presetTokens.map(tokenValue => (
          <div key={tokenValue} onDragOver={handleDragOver} onDrop={(e) => handleTokenDrop(e, tokenValue)} onDragEnter={(e) => { e.preventDefault(); setHoveredBlock(tokenValue); }} onDragLeave={(e) => { e.preventDefault(); setHoveredBlock(null); }}
               className={`flex-1 min-w-[150px] md:min-w-[180px] flex flex-col p-3 rounded-2xl border border-slate-100 shadow-sm transition ${hoveredBlock === tokenValue ? "bg-blue-50" : "bg-white/90"}`}>
            <div className="text-center font-bold mb-2 text-lg flex-shrink-0">{tokenValue}</div>
            <div className="flex-1 overflow-y-auto no-scrollbar space-y-1 -mr-1 pr-1" style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
              {groupedObjectives[tokenValue]?.length > 0 ? groupedObjectives[tokenValue].map(objective => renderObjectiveCard(objective)) : (<p className="text-center text-xs text-gray-500 pt-4">Glissez un objectif ici</p>)}
            </div>
          </div>
        ))}
        {/* "Other" column */}
        <div key="other" onDragOver={handleDragOver} onDrop={(e) => handleTokenDrop(e, "other")} onDragEnter={(e) => { e.preventDefault(); setHoveredBlock("other"); }} onDragLeave={(e) => { e.preventDefault(); setHoveredBlock(null); }}
             className={`flex-1 min-w-[150px] md:min-w-[180px] flex flex-col p-3 rounded-2xl border border-slate-100 shadow-sm transition ${hoveredBlock === "other" ? "bg-blue-50" : "bg-white/90"}`}>
          <div className="text-center font-bold mb-2 text-lg flex-shrink-0">Autre</div>
          <div className="mb-2 flex-shrink-0"><input type="number" value={customToken} onChange={(e) => setCustomToken(e.target.value)} placeholder="Tokens" className="border rounded p-1 w-full text-sm"/></div>
          <div className="flex-1 overflow-y-auto no-scrollbar space-y-1 -mr-1 pr-1" style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
            {groupedObjectives["other"]?.length > 0 ? groupedObjectives["other"].map(objective => renderObjectiveCard(objective)) : (<p className="text-center text-xs text-gray-500 pt-4">Glissez un objectif ici</p>)}
          </div>
        </div>
      </div>
      {/* Bottom Section: Unassigned Objectives */}
      <div className={backlogClass}
           onClick={handleBottomClick} onDrop={handleDropOnBottom} onDragOver={handleDragOver}
           style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
          <div className="space-y-1">
            {objectivesUnassigned.map(objective => renderObjectiveCard(objective))}
          </div>
          {inputObjective && (
            <div className="mt-2">
                <div className="relative">
                   <input autoFocus type="text" value={inputObjective.value} onChange={handleInputChange} onBlur={handleInputBlur} onKeyDown={handleInputKeyDown} className="border rounded p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-full" placeholder="Nom du nouvel objectif"/>
                   {/* --- NOUVEAU: Affichage suggestion de titre --- */}
                   {predictedTitle && !titleSuggestionDismissed && (
                     <div className="absolute -top-10 left-0 mt-1.5 w-full z-20">
                       <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 text-xs px-2 py-1.5 rounded-md shadow-sm flex items-center justify-between">
                         <button
                           type="button"
                           onClick={() => setInputObjective({ ...inputObjective, value: predictedTitle })}
                           className="flex items-center text-left hover:text-yellow-900 flex-grow"
                           title="Accepter la suggestion"
                         >
                           <Sparkles className="h-4 w-4 mr-2 flex-shrink-0" />
                           <span className="truncate">{predictedTitle}</span>
                         </button>
                         <button
                           type="button"
                           onClick={() => setTitleSuggestionDismissed(true)}
                           className="ml-2 p-0.5 rounded-full hover:bg-yellow-200"
                           title="Refuser"
                         >
                           <X className="h-3.5 w-3.5" />
                         </button>
                       </div>
                     </div>
                   )}
                   {/* --- FIN NOUVEAU --- */}
                </div>
            </div>
          )}
          {!inputObjective && objectivesUnassigned.length === 0 && (<div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400 pointer-events-none">Glissez ici pour désassigner (Effort 0) ou cliquez pour ajouter</div>)}
      </div>
    </div>
  );
};
export default KickoffTokensView;
// --- END OF FILE KickoffTokensView.js ---
